<script lang="ts" setup>
    import { onMounted, ref, reactive, computed, nextTick } from 'vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import TextInput from '../../components/TextInput.vue';
    import DropDownSplitButton from '../../components/DropDownSplitButton.vue'
    import DropDownButtonItem from '../../components/DropDownButtonItem.vue'
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import RadioButtonInput from "../../components/RadioButtonInput.vue"
    import Checkbox from "../../components/Inputs/Checkbox.vue"
    import Toolbar from "../../components/Toolbar.vue"
    import { ICol, IRowSelection, RenderModeType } from "../../lib/ChoiceTableHelpers";
    import { PrivilegeType, AppScreens } from "../../Enums/ChoiceEnums";
    import { LayoutOrientation, TableRowType } from "../../Enums/UiEnums";
    import { IPatientFlagSetupItem, PatientFlagType, iconFlagList, PatientFlagIconItem, PatientFlagIcon, colorFlagList, PatientFlagColorItem, PatientFlagColor,
            GetColorClass, GetIconClass, GetFlagColorItem, GetFlagIconItem, IPatientFlagSetupFlagItem, IPatientFlagSaveSetupItem } from '../../lib/Models/Setup/PatientFlagSetupModel';
    import { IToolbar, ToolbarStyle, ToolbarSize, ToolbarBackground } from "../../lib/Toolbar"

    import { ActionButtonStyle } from "../../lib/Types/Types";
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { confirmDialog, popAlert } from '../../lib/Notification';
    import '../../../css/choice.css';
    import '../../../css/patientFlag.css';
    import PatientFlagBadge from '../../components/Choice/Patient/PatientFlagBadge.vue';
    import { IPatientFlagBasicSettingsBase } from '../../lib/Models/Setup/PatientFlagSetupModel';
    import { timeout } from '../../lib/ChoiceUtils';


    const flagTableHeight = ref<number>();
    const setupTable = ref();
    const privilegesStore = usePrivilegesStore(initPinia());

    enum myToolbar {
        new, 
        delete,
        save
    }

    const toolbarConfiguration = ref<IToolbar>(
        {
            toolbarList: [
                {identifier: myToolbar.new, label: "New", action: ActionButtonStyle.Add, performAction: () => ToolbarNew()},
                {identifier: myToolbar.delete, label: "Delete", action: ActionButtonStyle.Delete, enabled: false, performAction: () => ToolbarDelete()},
                {identifier: myToolbar.save, label: "Save", action: ActionButtonStyle.Save, enabled: false, performAction: () => ToolbarSave()},
            ]
        }
    );

    const tableColumns = ref<ICol[]>([
        { Name: "", ExtraData: "RadioSelect" },
        { Name: 'Flag', DataField: 'empty' },
        { Name: 'Description', DataField: 'longDescription' },
        { Name: 'Active', DataField: 'active', RenderMode: RenderModeType.BoolYesNo },
    ]);

    const Description = ref<HTMLInputElement>();

    const props = defineProps<{
        patientFlagList?: IPatientFlagSetupItem[]
    }>();

    const formData = reactive<{
        tableData: IPatientFlagSetupItem[],
        flagSetupId?: number,
        shortDescription: string,
        longDescription: string,
        tooltip: string,
        colorId: PatientFlagColor,
        iconId?: PatientFlagIcon,
        flagType: PatientFlagType,
        active: boolean,
        firstAction: boolean,
        portalEmr: boolean,
        portalBilling: boolean,
        portalScheduler: boolean,
        portalDocuments: boolean,
        flagCount: number
    }>({
        tableData: [],
        longDescription: "",
        tooltip: "",
        flagType: PatientFlagType.Short,
        shortDescription: "",
        active: true,
        colorId: PatientFlagColor.Missing,
        firstAction: false,
        portalEmr: true,
        portalBilling: true,
        portalScheduler: true,
        portalDocuments: true,
        flagCount: 0
    });

    onMounted(async () => {
        await privilegesStore.getScreens([AppScreens.PatientFlagSetup]);
        if (props.patientFlagList == undefined) {
            formData.tableData = await ChoiceApi.patientFlagSetup.getSetupList() ?? [];
        }
        else {
            formData.tableData = props.patientFlagList;
        }

        nextTick(windowResize);
    });

    const updatePrivilege = computed<boolean>(() => (privilegesStore.screens[AppScreens.PatientFlagSetup] >= PrivilegeType.ReadUpdate && formData.firstAction) ? true : false);

    const GetIconSelectedItemClass = computed<string>(() => {
        if (null == formData.iconId) 
            return "";
        const selectedItem = GetFlagIconItem(formData.iconId);
        return selectedItem.class;
    });
    const GetColorSelectedItemClass = computed<string>(() => {
        if (null == formData.colorId) 
            return "";
        const selectedItem = GetFlagColorItem(formData.colorId);
        return selectedItem.class;
    });
    const GetColorSelectedItemId = computed<number>(() => {
        if (null == formData.colorId) 
            return 0;
        const selectedItem = GetFlagColorItem(formData.colorId);
        return selectedItem.id;
    });
    const colorClass = computed<string>(() => GetColorClass(formData.colorId));
    const iconClass = computed<string>(() => GetIconClass(formData.iconId ?? PatientFlagIcon.Missing, formData.colorId));

    function windowResize() {
        // Make sure the screen has updated before trying to measure the setup table
        timeout(500).then(setTableHeight);
    }

    async function setTableHeight()
    {
        while(true)
        {
            const rect = setupTable.value?.getBoundingClientRect();
            if (rect && rect.height)
            {
                flagTableHeight.value = window.innerHeight - (rect.top + 10);
                break;
            }
            await timeout(10);
        }
    }

    async function ToolbarNew() {
        toolbarConfiguration.value.toolbarList[myToolbar.save].enabled = true;
        toolbarConfiguration.value.toolbarList[myToolbar.delete].enabled = false;
        formData.firstAction = true;

        ClearFields();

        setTimeout(() => { 
            Description.value?.focus();
        }, 100);
    }

    function ClearFields() {
        formData.flagSetupId = undefined;
        formData.shortDescription = "";
        formData.longDescription = "";
        formData.tooltip = "";
        formData.colorId = PatientFlagColor.Missing;
        formData.iconId = PatientFlagIcon.Missing;
        formData.flagType = PatientFlagType.Short;
        formData.active = true;
        formData.portalBilling = true;
        formData.portalEmr = true;
        formData.portalDocuments = true;
        formData.portalScheduler = true;
        formData.flagCount = 0;
    }

    async function ToolbarDelete() {
        const confirmResp = await confirmDialog(`Are you sure you want to delete: ${formData.shortDescription ? formData.shortDescription : formData.longDescription}`);
        if (!confirmResp) { return; }

        const post_data: IPatientFlagSetupFlagItem = {
            flagSetupId: formData.flagSetupId ?? 0
        }
        const result = await ChoiceApi.patientFlagSetup.deleteSetup(post_data);
        if (result?.statusDescription) {
            popAlert(`*** Saved Failed: ${result.statusDescription}`);
            return;
        }
        let index = formData.tableData.findIndex(o => o.flagSetupId == formData.flagSetupId);
        if (index > -1) {
            formData.tableData.splice(index, 1);
            ClearFields();
            formData.firstAction = false;
            toolbarConfiguration.value.toolbarList[myToolbar.save].enabled = false;
            toolbarConfiguration.value.toolbarList[myToolbar.delete].enabled = false;
        }
    }

    async function ToolbarSave() {
        let errorMessage = new Array();
        if (formData.longDescription == "") {
            errorMessage.push("Description");
        }
        if (formData.tooltip == "") {
            errorMessage.push("Tooltip");
        }
        if (formData.colorId == PatientFlagColor.Missing) {
            errorMessage.push("Color");
        }
        if (formData.flagType == PatientFlagType.Short && formData.shortDescription == "") {
            errorMessage.push("Short Description");
        }
        if (formData.flagType == PatientFlagType.Icon && formData.iconId == PatientFlagIcon.Missing) {
            errorMessage.push("Icon");
        }
        if (errorMessage.length > 0) {
            popAlert(`Missing Required Fields: ${errorMessage.join(", ")}`);
            return;
        }

        const post_data: IPatientFlagSaveSetupItem = {
            flagSetupId: formData.flagSetupId,
            shortDescription: formData.shortDescription,
            longDescription: formData.longDescription,
            tooltip: formData.tooltip,
            colorId: formData.colorId,
            iconId: formData.iconId,
            flagStyleIcon: formData.flagType == PatientFlagType.Icon ? true : false,
            active: formData.active,
            emrPortalActive: formData.portalEmr,
            billingPortalActive: formData.portalBilling,
            schedulerPortalActive: formData.portalScheduler,
            documentPortalActive: formData.portalDocuments
        }
        const result = await ChoiceApi.patientFlagSetup.saveSetup(post_data);
        if (result?.errorMessage) {
            popAlert(`*** Saved Failed: ${result.errorMessage}`);
            return;
        }
        let index = formData.tableData.findIndex(o => o.flagSetupId == result?.flagSetupId);
        if (index < 0) {
            formData.flagSetupId = result?.flagSetupId;
            index = formData.tableData.length; 
        }
        formData.tableData[index] = {
            flagSetupId: result?.flagSetupId ?? 0, 
            shortDescription: formData.shortDescription, 
            longDescription: formData.longDescription, 
            tooltip: formData.tooltip, 
            colorId: formData.colorId, 
            iconId: formData.iconId, 
            flagStyleIcon: formData.flagType == PatientFlagType.Icon ? true : false,
            active: formData.active, 
            emrPortalActive: formData.portalEmr,
            billingPortalActive: formData.portalBilling,
            schedulerPortalActive: formData.portalScheduler,
            documentPortalActive: formData.portalDocuments,
            flagCount: formData.flagCount            
        };
    }

    function tableRowClick(rowData : IRowSelection, _rowType: TableRowType, _evt: MouseEvent) {
        toolbarConfiguration.value.toolbarList[myToolbar.save].enabled = true;
        formData.firstAction = true;
        const localRowData = (rowData as IPatientFlagSetupItem & IRowSelection);
        toolbarConfiguration.value.toolbarList[myToolbar.delete].enabled = localRowData.flagCount > 0 ? false : true;
        formData.flagSetupId = localRowData.flagSetupId;
        formData.longDescription = localRowData.longDescription;
        formData.tooltip = localRowData.tooltip;
        formData.flagType = localRowData.flagStyleIcon ? PatientFlagType.Icon : PatientFlagType.Short;
        formData.shortDescription = localRowData.shortDescription;
        formData.active = localRowData.active;
        formData.colorId = localRowData.colorId;
        formData.iconId = localRowData.iconId;
        formData.portalEmr = localRowData.emrPortalActive;
        formData.portalBilling = localRowData.billingPortalActive;
        formData.portalScheduler = localRowData.schedulerPortalActive;
        formData.portalDocuments = localRowData.documentPortalActive;
        formData.flagCount = localRowData.flagCount;
    }
    function OnClickFontDropdown(data: PatientFlagIconItem) {
        formData.iconId = data.id;
    }
    function OnClickColorDropdown(data: PatientFlagColorItem) {
        formData.colorId = data.id;
    }

    const currentFlagSettings = computed<IPatientFlagBasicSettingsBase | undefined>(() => {
        if (!formData.flagSetupId)
            return;

        return {
            shortDescription: formData.shortDescription,
            longDescription: formData.longDescription,
            tooltip: formData.tooltip,
            colorId: formData.colorId,
            iconId: formData.iconId,
            flagStyleIcon: formData.flagType === PatientFlagType.Icon,
            active: formData.active
        };
    });
</script>

<style scoped>
    .fixedTableWidth {
        width: 400px;
    }
    .fixedColumnActive {
        width: 150px;
    }
    .fixedColumnEdit {
        width: 300px;
    }
    .fixedColumnShort {
        width: 80px;
    }
    .fixedColumnPortal {
        width: 120px;
    }
</style>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="row mb-0 mt-0">
        <div class="fixedTableWidth isalus-screen-header">
            Patient Flag Setup 
        </div>
        <div class="col">
            <Toolbar 
              :configuration="toolbarConfiguration" 
              :toolbarSize="ToolbarSize.regular" 
              :toolbarStyle="ToolbarStyle.outline"
              :toolbarBackground="ToolbarBackground.white"/>            
        </div>
    </div>
    <div class="row">
        <div class="fixedTableWidth">
            <p style="font-size: 0.75rem;">
                <ChoiceTable 
                    ref="setupTable"
                    :columns="tableColumns" 
                    :data="formData.tableData" 
                    :show-filters="false" 
                    :allowSorting="false"
                    :height="flagTableHeight"
                    @rowclick="tableRowClick">
                    <template #cell="{col, row, value}">
                        <template v-if="col.ExtraData === 'RadioSelect'">
                            <RadioButtonInput :value="row.flagSetupId" v-model:model-value="formData.flagSetupId" />
                        </template>
                        <template v-if="'Flag' === col.Name">
                            <template v-if="!row.flagStyleIcon">
                                <span :class="GetColorClass(row.colorId)">{{ row.shortDescription }}</span>
                            </template>
                            <template v-if="row.flagStyleIcon">
                                <span :class="GetIconClass(row.iconId, row.colorId)"></span>
                            </template>
                        </template>
                    </template>
                </ChoiceTable>
            </p>
        </div>
        <div class="col" v-if="formData.flagSetupId || formData.firstAction">
            <div class="row mt-2">
                <div class="fixedColumnActive">
                    <Checkbox v-model="formData.active" :disabled="!updatePrivilege"/><span class="label-isalus-small">Active</span>
                </div>
                <div class="col-auto label-isalus-small mt-1">
                    Preview:
                </div>
                <div class="col-auto label-isalus-small mt-1">
                    <PatientFlagBadge :flag="currentFlagSettings" />
                </div>
            </div>
            <div class="row mt-2">
                <div class="fixedColumnEdit label-isalus-small">
                    Description
                </div>
            </div>
            <div class="row">
                <div class="fixedColumnEdit">
                    <TextInput 
                        ref="Description"
                        v-model:text-value="formData.longDescription" 
                        :maxLength="40"
                        placeholder="Please enter a description"
                        :disabled="!updatePrivilege"
                        cssFramework = "vanilla" />
                </div>
            </div>
            <div class="row mt-2">
                <div class="fixedColumnEdit label-isalus-small">
                    Tooltip
                </div>
            </div>
            <div class="row">
                <div class="fixedColumnEdit">
                    <TextInput 
                        v-model:text-value="formData.tooltip" 
                        :maxLength="125"
                        placeholder="Please enter a tooltip"
                        :disabled="!updatePrivilege"
                        cssFramework = "vanilla" />
                </div>
            </div>
            <div class="row mt-2">
                <div class="col label-isalus-small">
                    Color
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <DropDownSplitButton :disabled="!updatePrivilege" :baseClass="'btn btn-outline-secondary label-isalus-small'">
                        <template v-slot:button>
                            <span class="label-isalus-small ps-1 pe-1" :class="GetColorSelectedItemClass">{{ PatientFlagColor[GetColorSelectedItemId] }}</span>
                        </template>
                        <DropDownButtonItem v-for="colorItem in colorFlagList" v-bind:data="colorItem" :baseClass="'dropdown-item isalus-padding-vertical-0'" v-on:click="OnClickColorDropdown">
                            <span class="label-isalus-small ps-1 pe-1" :class="colorItem.class">{{ PatientFlagColor[colorItem.id] }}</span>
                        </DropDownButtonItem>
                    </DropDownSplitButton>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col label-isalus-small">
                    Portal Availability
                </div>
            </div>
            <div class="row mt-2">
                <div class="fixedColumnPortal">
                    <Checkbox v-model="formData.portalEmr" :disabled="!updatePrivilege"/><span class="label-isalus-small">EMR</span>
                </div>
                <div class="fixedColumnPortal">
                    <Checkbox v-model="formData.portalScheduler" :disabled="!updatePrivilege"/><span class="label-isalus-small">iScheduler</span>
                </div>
            </div>
            <div class="row mt-1">
                <div class="fixedColumnPortal">
                    <Checkbox v-model="formData.portalBilling" :disabled="!updatePrivilege"/><span class="label-isalus-small">Billing</span>
                </div>
                <div class="fixedColumnPortal">
                    <Checkbox v-model="formData.portalDocuments" :disabled="!updatePrivilege"/><span class="label-isalus-small">eDocuments</span>
                </div>
            </div>
            <div class="row mt-4">
                <div class="col">
                    <RadioButtonInput :value="PatientFlagType.Short" v-model="formData.flagType" :layout="LayoutOrientation.Horz" :disabled="!updatePrivilege">                    
                        <div class="label-isalus-small">Short Flag</div>
                    </RadioButtonInput>
                    <RadioButtonInput :value="PatientFlagType.Icon" v-model="formData.flagType" :layout="LayoutOrientation.Horz" :disabled="!updatePrivilege">
                        <div class="label-isalus-small">Icon Flag</div>
                    </RadioButtonInput>
                </div>
            </div>
            <template v-if="formData.flagType == PatientFlagType.Short">
                <div class="row mt-2">
                    <div class="label-isalus-small">
                        Short Description
                    </div>
                </div>
                <div class="row">
                    <div class="fixedColumnShort">
                        <TextInput 
                            v-model:text-value="formData.shortDescription" 
                            :maxLength="3"
                            placeholder=""
                            :disabled="!updatePrivilege"
                            cssFramework = "vanilla" />
                    </div>
                </div>
            </template>
            <template v-if="formData.flagType == PatientFlagType.Icon">
                <div class="row mt-2">
                    <div class="col label-isalus-small">
                        Icon
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <DropDownSplitButton :disabled="!updatePrivilege" :baseClass="'btn btn-outline-secondary label-isalus-small'" :dropdownStyle="'height: 250px; overflow-y:scroll'">
                            <template v-slot:button>
                                <span :class="GetIconSelectedItemClass" style="color: black"></span>
                            </template>
                            <DropDownButtonItem v-for="iconFlagItem in iconFlagList" v-bind:data="iconFlagItem" :baseClass="'dropdown-item'" v-on:click="OnClickFontDropdown">
                                <span :class="iconFlagItem.class" class="me-1"></span>-<span class="ms-1">{{ iconFlagItem.label }}</span>
                            </DropDownButtonItem>
                        </DropDownSplitButton>
                    </div>
                </div>
            </template>
            <div class="row mt-2">
                <div class="col label-isalus-small fst-italic">
                    {{ formData.flagCount }} Patients tied to flag.
                </div>
            </div>
        </div>
    </div>
</template>

